import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import type { Reservation } from '../store/useStore'
import { mapApiReservation } from './reservationMap'

// #1024 (F1b): reservations の Firestore 公開直読は PII / 決済ID の全件漏洩に
// つながるため廃止。onSnapshot のリアルタイム購読を API ポーリングに置換した。
// 店舗側の予約一覧は GET /reservations?storeId= に一本化（owner|manager 対応）。
const POLL_INTERVAL_MS = 15_000

/** JST の今日 (YYYY-MM-DD)。予約の date は店の営業日として JST で入っている。 */
function todayJst(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date())
}

/**
 * 指定店舗の予約一覧（店舗側）。
 * かつては onSnapshot でリアルタイム購読していたが、#1024 で API 経由に一本化。
 * 新規リクエスト通知は最大 POLL_INTERVAL_MS 遅延する（画面復帰時は即時再取得）。
 * オーナー/店長の予約設定タブや新規リクエスト通知で使用。
 */
export function useStoreReservations(storeId: string | null | undefined) {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(!!storeId)

  // Reset state when the target store changes (derive-state-from-props).
  const [trackedStoreId, setTrackedStoreId] = useState(storeId)
  if (storeId !== trackedStoreId) {
    setTrackedStoreId(storeId)
    setReservations([])
    setLoading(!!storeId)
  }

  // 進行中フェッチのレースを避けるため世代トークンで最新のみ採用。
  const genRef = useRef(0)

  const fetchOnce = useCallback(async () => {
    if (!storeId) return
    const gen = ++genRef.current
    try {
      const res = await api.getStoreReservations(storeId)
      if (gen !== genRef.current) return
      const list: Reservation[] = (res?.reservations ?? []).map(mapApiReservation)
      setReservations(list)
    } catch (err) {
      if (gen !== genRef.current) return
      console.error('[useStoreReservations] fetch error:', err)
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [storeId])

  useEffect(() => {
    if (!storeId) return
    fetchOnce()
    const id = window.setInterval(fetchOnce, POLL_INTERVAL_MS)
    const onFocus = () => {
      if (document.visibilityState === 'visible') fetchOnce()
    }
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [storeId, fetchOnce])

  const byTime = (a: Reservation, b: Reservation) =>
    new Date(`${a.date}T${a.time}:00+09:00`).getTime() -
    new Date(`${b.date}T${b.time}:00+09:00`).getTime()

  const pending = reservations.filter((r) => r.status === 'pending').sort(byTime)

  // #1374 来店マークは**当日の現場作業**。未来の予約が混ざったリストから探させると、
  // 今日の 2 件を見つけるのにスクロールが要る。日付で切って専用のタブに出す。
  const today = todayJst()
  const approvedList = reservations.filter((r) => r.status === 'approved')

  const todays = approvedList.filter((r) => r.date === today).sort(byTime)
  // 明日以降。以前はここに今日も過去も混ざっており、しかも未ソートだった
  // (7/30 → 7/31 → 7/28 の順で並んでいた)。
  const approved = approvedList.filter((r) => r.date > today).sort(byTime)
  // 昨日以前の approved = 店主が来店マークを押し忘れたもの。承認済みに溜まり続けて
  // 今日の予約に紛れていた。埋もれないよう別枠にする。
  // (#1374 で売上は予約日から導出するので、押し忘れても金額は狂わない。記録の抜け)
  const unmarked = approvedList.filter((r) => r.date < today).sort(byTime)

  // #738 B-1: visited / no_show は履歴扱い (= 来店マーク後 / 未来店マーク後の終端)
  const archived = reservations
    .filter((r) => ['rejected', 'cancelled', 'expired', 'visited', 'no_show'].includes(r.status))
    .sort((a, b) => byTime(b, a))  // 直近の出来事から見る

  // #1386 カレンダー用。**タブの絞り込みとは目的が違う。**
  // タブは「今からさばく仕事」を出すので今日・明日以降・押し忘れに分けるが、
  // カレンダーは週の埋まり具合を俯瞰するので**全部入れる**。
  // #1374 でタブを分けたとき、カレンダーが `approved` (明日以降) を見たままになり、
  // **今日の予約がカレンダーから消えていた** (一覧には出るのにカレンダーは 0 件)。
  const calendar = [...todays, ...approved, ...unmarked].sort(byTime)

  return {
    reservations, pending, todays, approved, unmarked, archived, calendar,
    loading, refetch: fetchOnce,
  }
}
